<template>
  <div class="row">
    <div class="col-12">
      <page-header
        :page="page"
        :actions="headerActionConfig"
        right-side-class="col-xl-4 align-items-end d-flex pb-2 justify-content-end"
      />

      <filter-manager
        v-model="filter"
        class="rounded"
        icon-button="fas fa-search"
        search-container-class="mt-md-2 pt-md-4"
        :filters="filtersHeader"
        :display-advanced="false"
        @search="getHistory"
        @type="loadDiscipline($event)"
      />

      <custom-separator
        text="Resultado de Evaluación"
        class="my-4 custom-separator-line"
      />

      <custom-result-not-found
        v-if="!evaluations.length"
        text="No hay información disponible."
      />

      <template v-else>
        <student-evaluation-history-list
          :evaluations="evaluations"
          @refresh-evaluations="getHistory"
        />
        <pagination
          v-if="pagination.totalItemCount > pagination.pageSize"
          :data="pagination"
          show-text
          @pagination-go-page="goToPage"
        />
      </template>
    </div>
  </div>
</template>

<script>
import Pagination from 'pagination';
import FilterManager from 'FilterManager';
import CustomResultNotFound from 'custom-result-not-found';
import mixAlert from '@/mixin/alert.mixin';
import mixWait from '@/mixin/loading.mixin';
import store from '@/store/index';
import PageHeader from 'PageHeader';
import CustomSeparator from 'custom-separator';
import { getStudentEvaluationAcademicYears } from '@/modules/common/academicyear.api.js';
import {
  getEvaluationSourceTypes,
  getEvaluationTypes,
  getCodeDisciplineTypes,
} from '@/modules/common/code.api.js';
import { getHistory } from '@/modules/student-evaluations/api.js';
import evaluationSourceTypes from '@/utils/constants/student/evaluationSourceTypes';
import appointmentStatusEnum from '@/utils/constants/student/appointmentStatusEnum';
import StudentEvaluationHistoryList from './components/StudentEvaluationHistoryList';

const PreschoolInterventionResultSummaryOption = {
  disabled: false,
  id: -1,
  isDirty: false,
  keyId: "00000000-0000-0000-0000-000000000000",
  name: "Informe de resultados de la intervención pre-escolar (Salida)",
  selected: false,
  sortOrder: 0
};

const PRUEBAS_ACADEMICAS_ID = 5;
const SCREEEN_HISTORY_TEVALUATION = 10;
export default {
  name: 'StudentEvaluationHistory',
  mixins: [mixWait, mixAlert],
  components: {
    FilterManager,
    PageHeader,
    StudentEvaluationHistoryList,
    CustomSeparator,
    CustomResultNotFound,
    Pagination,
  },
  data: () => ({
    page: {
      icon: 'fas fa-file-alt',
      title: 'Estudiante: Evaluaciones',
      routes: ['Inicio', 'Estudiante', 'Evaluaciones'],
    },
    filter: {
      academicYear: null,
      type: null,
      source: null,
      disciplineTypeId: null,
    },
    options: {
      academicYears: [],
      types: [],
      sources: [],
      disciplines: [],
    },
    pagination: {
      currentPage: 1,
      totalItemCount: 0,
      pageCount: 1,
      count: 0,
      pageSize: 10,
    },
    evaluations: [],
  }),
  computed: {
    headerActionConfig() {
      return [
        {
          text: 'Regresar',
          icon: 'fas fa-arrow-circle-left',
          variant: 'outline-success',
          click: () => this.goToStudentMenu(),
        },
        {
          text: 'Añadir',
          icon: 'fas fa-file-plus',
          variant: 'success',
          visible: this.canAddEvaluation,
          click: () => this.goToAddEvaluation(),
        },
      ];
    },
    filtersHeader() {
      return [
        {
          name: 'academicYear',
          label: 'Año Académico:',
          placeholder: 'Seleccione',
          component: 'CustomDropDown',
          options: this.options.academicYears,
          fieldName: 'description',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
        },
        {
          name: 'type',
          label: 'Tipo:',
          placeholder: 'Seleccione',
          component: 'CustomDropDown',
          options: this.options.types,
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
        },
        {
          name: 'disciplineTypeId',
          label: 'Disciplina:',
          placeholder: 'Seleccione',
          component: 'CustomDropDown',
          options: this.options.disciplines,
          disabled: !this.options.disciplines.length,
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
        },
        {
          name: 'source',
          label: 'Fuente:',
          placeholder: 'Seleccione',
          component: 'CustomDropDown',
          options: this.options.sources,
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
        },
      ];
    },
    canAddEvaluation() {
      return store.getters.HasPermission('student.evaluation.search.addbutton');
    },
  },
  async created() {
    await Promise.all([
      this.loadAcademicYears(),
      this.loadSources(),
      this.loadTypes(),
      this.getHistory(),
    ]);
  },
  methods: {
    async loadAcademicYears() {
      const studentSieId = store.getters.SelectedStudent.studentId;
      const { data } = await getStudentEvaluationAcademicYears(studentSieId);
      this.options.academicYears = [...data];
    },
    async loadSources() {
      const { data } = await getEvaluationSourceTypes();
      this.options.sources = [...data];
    },
    async loadTypes() {
      const { data } = await getEvaluationTypes();
      this.options.types = [...data];
    },
    async loadDiscipline(type) {
      this.options.disciplines = [];
      this.filter.disciplineTypeId = null;
      if (!type) return;
      const { data } = await getCodeDisciplineTypes(type.id,SCREEEN_HISTORY_TEVALUATION);
      let options = data;

      if (type.id == PRUEBAS_ACADEMICAS_ID) {
        options.push(PreschoolInterventionResultSummaryOption);
        options = options.sort((a,b) => a.name.localeCompare(b.name));
      }

      this.options.disciplines = [...options];
    },
    async getHistory() {
      const params = {
        studentSieId: store.getters['$_student/StudentSie'],
        year: this.filter.academicYear?.id,
        pageNumber: this.pagination.currentPage,
        pageSize: this.pagination.pageSize,
        disciplineTypeId: this.filter.disciplineTypeId?.id,
        serviceTypeId: this.filter.type?.id,
        source: this.filter.source?.id,
      };
      const { data: res } = await getHistory(params);

      this.evaluations =
        res.data.filter(
          (x) =>
            !(
              x.sourceId == evaluationSourceTypes.MiPE_Cita &&
              (x.appointmentStatusId == appointmentStatusEnum.cancelled_Prov ||
                x.appointmentStatusId == appointmentStatusEnum.cancelled_De)
            )
        ) || [];

      this.$store.dispatch(
        'student-evaluation-history/setEvaluations',
        this.evaluations
      );

      this.pagination = {
        ...this.pagination,
        currentPage: res.currentPage,
        count: res.count,
        pageCount: res.pageCount,
        totalItemCount: res.totalItemCount,
      };
    },
    async goToPage(page) {
      this.pagination.currentPage = page;
      await this.getHistory();
    },
    goToAddEvaluation() {
      this.$router.push({
        name: 'student-evaluation-history-add',
        query: {
          from: this.$route.query.from,
        },
      });
    },
    goToStudentMenu() {
      this.$router.push({
        name: 'student-menu',
        query: {
          from: this.$route.query.from,
        },
      });
    },
  },
};
</script>

<style scoped>
.block-spacing {
  margin-bottom: 30px;
}

.block-spacing >>> .invalid {
  position: absolute;
  font-size: 13px;
  margin-left: 5px;
}
</style>
